import { DateTime } from 'luxon';

import { FilterKey, MenuItem, Mode } from '../ComponentLibrary/src';
import { TimelineItemType } from '../components/System/util';

export const defaultDataSystemFilter: FilterKey[] = [
  {
    id: 'sysId',
    label: 'S/N',
    selected: false,
    mode: Mode.single,
  },
];

// TODO get labels from the backend, maybe other fields too
export const defaultSystemFilter: FilterKey[] = [
  {
    id: 'stats.state',
    label: 'Status',
    selected: false,
    searchable: false,
  },
  {
    id: 'sysId',
    label: 'Serial No.',
    selected: false,
  },
  {
    id: 'site._id',
    textSearchId: 'site.name',
    label: 'Site',
    selected: false,
  },
  {
    id: 'systemDescription',
    label: 'Description',
    selected: false,
  },
  {
    id: 'org._id',
    textSearchId: 'org.name',
    label: 'Organization',
    selected: false,
  },
  {
    id: 'distributor._id',
    textSearchId: 'distributor.name',
    label: 'Distributor',
    selected: false,
  },
  {
    id: 'stats.systemHours',
    label: 'Runtime (hrs)',
    selected: false,
    mode: Mode.range,
  },
  {
    id: 'latest.accAir',
    label: 'Total Accumulated Air (scf)',
    selected: false,
    mode: Mode.range,
  },
  {
    id: 'stats.flow1Hours',
    label: 'Air Flow (scfm)',
    selected: false,
    mode: Mode.range,
  },
  {
    id: 'latest.pressure',
    label: 'Air Pressure (psi)',
    selected: false,
    mode: Mode.range,
  },
  {
    id: 'shipDate',
    label: 'Ship Date',
    selected: false,
    range: {
      from: DateTime.now().minus({ weeks: 1 }).toJSDate(),
      to: DateTime.now().toJSDate(),
    },
    mode: Mode.dateRange,
  },
  {
    id: 'commissionDate',
    label: 'Commission Date',
    selected: false,
    range: {
      from: DateTime.now().minus({ weeks: 1 }).toJSDate(),
      to: DateTime.now().toJSDate(),
    },
    mode: Mode.dateRange,
  },
  {
    id: 'model',
    label: 'Model',
    selected: false,
    searchable: false,
  },
  {
    id: 'productFamily',
    label: 'Product Family',
    selected: false,
    searchable: false,
  },
  {
    id: 'lastSeen',
    label: 'Last Seen',
    selected: false,
    range: {},
    mode: Mode.dateRange,
  },
  {
    id: 'subscriptionStatus',
    label: 'Subscription',
    selected: false,
    searchable: false,
  },
  {
    id: 'faultCodes',
    label: 'Active Event Codes',
    selected: false,
  },
];

export const TimelineItemsPretty = {
  [TimelineItemType.faultCleared]: 'Event Code Cleared',
  [TimelineItemType.shipDate]: 'Shipped',
  [TimelineItemType.commissionDate]: 'Commissioned',
  [TimelineItemType.fault]: 'Event Code',
  [TimelineItemType.stateChange]: 'State Change',
};

export const timelineItemTypes: MenuItem[] = [
  {
    id: TimelineItemType.commissionDate,
    label: 'Commission Date',
    selected: false,
  },
  {
    id: TimelineItemType.fault,
    label: 'Event Codes',
    selected: false,
  },
  {
    id: TimelineItemType.faultCleared,
    label: 'Event Codes Cleared',
    selected: false,
  },
  {
    id: TimelineItemType.shipDate,
    label: 'Ship Date',
    selected: false,
  },
  {
    id: TimelineItemType.stateChange,
    label: 'State Changes',
    selected: false,
  },
];

export const defaultTimelineFilter: FilterKey[] = [
  {
    id: 'evtType',
    label: 'Type',
    selected: false,
  },
  {
    id: 'ts',
    label: 'Date',
    selected: false,
    range: { from: DateTime.now().toJSDate(), to: DateTime.now().toJSDate() },
    mode: Mode.dateRange,
  },
  {
    id: 'data.faultId',
    label: 'Event Code',
    selected: false,
    mode: Mode.multi,
  },
];

export const notificationTypes = [
  {
    id: 'FAULT',
    label: 'Fault Events',
    tooltip: 'When a fault event is triggered on a system.',
  },
  {
    id: 'WARNING',
    label: 'Warning Events',
    tooltip: 'When a warning event is triggered on a system.',
  },
  {
    id: 'INFO',
    label: 'Informational Events',
    tooltip: 'When an informational event is triggered on a system.',
  },
  {
    id: 'OFFLINE',
    label: 'System Went Offline',
    tooltip: 'When a system which was online goes offline.',
  },
  {
    id: 'ONLINE',
    label: 'System Came Online',
    tooltip: 'When a system which was offline comes online.',
  },
  {
    id: 'ACTIONLOG',
    label: 'Action Log Posted',
    tooltip: 'When an action log is posted by a user on a system.',
  },
  {
    id: 'SUBSCRIPTION',
    label: 'Subscription Expiration',
    tooltip: 'When a system subscription will or has expired.',
  },
  {
    id: 'WARRANTY',
    label: 'Warranty Expiration',
    tooltip: 'When a system warranty will or has expired.',
  },
];

export const SMARTVIEW_API_URL = 'https://api.dashboard.qnergy.com/v1';
export const S3_URL = process.env.REACT_APP_S3_HOST || 'https://uploads.qnergy.com';
export const SMARTVIEW_ACCOUNTS_URL = 'https://accounts.qnergy.com';

export const PERMISSIONS = {
  dashboard: {
    actionLogs: {
      add: 'qnergyview:actionLogs:add',
      read: 'qnergyview:actionLogs:read',
      uploadPhotos: 'qnergyview:actionLogs:uploadPhotos',
    },
    distributors: {
      add: 'qnergyview:distributors:add',
      delete: 'qnergyview:distributors:delete',
      update: 'qnergyview:distributors:update',
      read: 'qnergyview:distributors:read',
    },
    orgs: {
      add: 'qnergyview:orgs:add',
      delete: 'qnergyview:orgs:delete',
      update: 'qnergyview:orgs:update',
    },
    sites: {
      add: 'qnergyview:sites:add',
      delete: 'qnergyview:sites:delete',
      update: 'qnergyview:sites:update',
    },
    systems: {
      advancedRead: 'qnergyview:systems:advancedRead',
      update: 'qnergyview:systems:update',
      advancedUpdate: 'qnergyview:systems:advancedUpdate',
      updateProductAccess: 'qnergyview:systems:updateProductAccess',
      updateDistributor: 'qnergyview:systems:updateDistributor',
      updateOrg: 'qnergyview:systems:updateOrg',
      updateSite: 'qnergyview:systems:updateSite',
      updateShipDate: 'qnergyview:systems:updateShipDate',
      read: 'qnergyview:systems:read',
      isSubscriptionExempt: 'qnergyview:systems:isSubscriptionExempt',
      hideStates: 'qnergyview:systems:hideStates',
    },
    othersActionLogs: {
      delete: 'qnergyview:othersActionLogs:delete',
      update: 'qnergyview:othersActionLogs:update',
    },
    ownActionLogs: {
      delete: 'qnergyview:ownActionLogs:delete',
      update: 'qnergyview:ownActionLogs:update',
    },
    developerOptions: {
      all: 'qnergyview:developerOptions:*',
    },
    faults: {
      update: 'qnergyview:faults:update',
      readAdvanced: 'qnergyview:faults:readAdvanced',
    },
    newFeatures: {
      read: 'qnergyview:newFeatures:read',
    },
    users: {
      updateAccess: 'qnergyview:users:updateAccess',
      updateDistributor: 'qnergyview:users:updateDistributor',
    },
  },
  qnergy: {
    users: {
      delete: 'qnergy:users:delete',
      read: 'qnergy:users:read',
      create: 'qnergy:users:create',
      update: 'qnergy:users:update',
      updateRoles: 'qnergy:users:updateRoles',
      invite: 'qnergy:users:invite',
    },
  },
};

export const DEFAULT_GAS_COMP = {
  CH4: 90,
  CO2: 0,
  OTHER: 10,
  GER: 1.2977,
};

export const SUBSCRIPTION_STATUSES = {
  expired: 'Expired',
  active: 'Active',
  willExpireSoon: 'Will Expire Soon',
};

export const MAX_UPLOAD_IMG_WIDTH = 1920;
export const MAX_UPLOAD_AMOUNT = 25;
export const IMAGE_FALLBACK =
  'data:image/png;base64,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';
